/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import ReactSelect from "react-select/lib/Creatable";
import classnames from "classnames/bind";
import { hasErrors } from "utils";

import styles from "./select.module.css";
import { Props as SelectProps } from "react-select/lib/Creatable";
import { CustomDropdownIndicator } from "./custom-dropdown-indicator";
import FieldLabel from "components/field-label/field-label";
import ErrorMessage from "components/error-message/error-message";
import defaultStyles from "./default-styles";

/*

THIS FILE IS DUPLICATED FOR EVERY SELECT COMPONENT - Select, Async, Creatable, AsyncCreatable.

IF YOU ARE CHANGING ONE, PLEASE CHANGE ALL OTHERS.

OTHERWISE, FIX TYPESCRIPT GENERICS.

*/

interface OwnProps {
  label?: string;
  helpText?: string;
  errorMessage?: string;
  classname?: string;
  hideError?: boolean;
  isClearable?: boolean;
  autoFocus?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  createLabel?: string;
  hideOptions?: boolean;
  hideDropdownIndicator?: boolean;
  onInputChange?: (newValue: string) => void;
}

const cx = classnames.bind(styles);

type DefaultProps<OptionType> = Pick<SelectProps<OptionType>, "components">;
type Props<OptionType> = DefaultProps<OptionType> & OwnProps & SelectProps<OptionType>;
type StateProps<OptionType> = Pick<SelectProps<OptionType>, "components" | "placeholder"> & State;

type State = {
  inputValue: string;
};

export default class Select<OptionType> extends React.Component<Props<OptionType>, StateProps<OptionType>> {
  // TODO: defaultProps is untyped as of now.
  // See https://github.com/Microsoft/TypeScript/issues/24018
  // See https://github.com/Microsoft/TypeScript/issues/14600
  static defaultProps: DefaultProps<any> = {
    components: { DropdownIndicator: CustomDropdownIndicator }
  };

  constructor(props: Props<OptionType>) {
    super(props);
    this.state = {
      components: this.getComponents(),
      placeholder: this.getPlaceholder(),
      inputValue: ""
    };
  }

  componentDidUpdate(prevProps: Props<OptionType>) {
    if (
      prevProps.isDisabled !== this.props.isDisabled ||
      prevProps.components !== this.props.components ||
      prevProps.placeholder !== this.props.placeholder
    ) {
      this.setState({
        components: this.getComponents(),
        placeholder: this.getPlaceholder()
      });
    }
  }

  getComponents() {
    return {
      ...this.props.components,
      ...(this.props.isDisabled && {
        MultiValueRemove: () => null,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null
      })
    };
  }

  getPlaceholder() {
    if (this.props.isDisabled) return " ";
    return this.props.placeholder;
  }

  handleInputValueChange(newValue) {
    this.setState({
      inputValue: newValue
    });
    return this.props.onInputChange && this.props.onInputChange(newValue);
  }

  render() {
    const {
      label,
      helpText,
      errorMessage,
      hideError,
      classname,
      components,
      isClearable = false,
      autoFocus = false,
      createLabel = "Create",
      hideOptions = false,
      hideDropdownIndicator = false,
      onInputChange,
      ...rest
    } = this.props as Props<OptionType>;
    const selectClassNames = cx("select-container", "container", classname, {
      "has-error": errorMessage && hasErrors(errorMessage, this.props.value)
    });
    const message = errorMessage && hasErrors(errorMessage, this.props.value) ? errorMessage : "";
    return (
      <div className={selectClassNames} data-test-id="select-container">
        {label && <FieldLabel htmlFor={`react-select-wrapper-title-${label}`} label={label} />}
        <div data-test-id="react-select-wrapper">
          <ReactSelect
            {...(hideOptions ? { noOptionsMessage: () => null } : {})}
            value={this.props.value}
            id={`react-select-${label}`}
            {...rest}
            classNamePrefix="react-select"
            className={styles["select-field"]}
            components={{ ...this.state.components, ...(hideDropdownIndicator && { DropdownIndicator: () => null }) }}
            isClearable={isClearable}
            autoFocus={autoFocus}
            styles={defaultStyles}
            placeholder={this.state.placeholder}
            formatCreateLabel={(userInput) => `${createLabel} "${userInput}"`}
            onInputChange={this.handleInputValueChange.bind(this)}
            inputValue={this.state.inputValue}
          />
        </div>
        {helpText && !errorMessage && (
          <span className={styles["react-select-wrapper-help-text"]} data-test-id="react-select-wrapper-help-text">
            {helpText}
          </span>
        )}
        {!hideError && (
          <div className={cx("error", classname)}>
            <ErrorMessage message={message} />
          </div>
        )}
      </div>
    );
  }
}
